import { format } from 'date-fns';
import { arSA, bn, enUS, hi, type Locale } from 'date-fns/locale';

import type { AppLanguage } from '../i18n/app';

const INTL_LOCALE_MAP: Record<AppLanguage, string> = {
  en: 'en-US',
  ar: 'ar-SA',
  ur: 'ur-PK',
  hi: 'hi-IN',
  bn: 'bn-BD',
  si: 'si-LK',
  ne: 'ne-NP',
};

const DATE_FNS_LOCALE_MAP: Partial<Record<AppLanguage, Locale>> = {
  en: enUS,
  ar: arSA,
  hi,
  bn,
};

export function getAppLanguageCode(language: string): AppLanguage {
  const code = language.split('-')[0] as AppLanguage;
  return code in INTL_LOCALE_MAP ? code : 'en';
}

export function getIntlLocale(language: string): string {
  return INTL_LOCALE_MAP[getAppLanguageCode(language)];
}

export function getDateFnsLocale(language: string): Locale {
  return DATE_FNS_LOCALE_MAP[getAppLanguageCode(language)] ?? enUS;
}

function usesIntlDateFormatting(language: string): boolean {
  return !DATE_FNS_LOCALE_MAP[getAppLanguageCode(language)];
}

/** Long date (dashboard period labels). */
export function formatLocalizedDate(date: Date, language: string): string {
  if (usesIntlDateFormatting(language)) {
    return new Intl.DateTimeFormat(getIntlLocale(language), { dateStyle: 'long' }).format(date);
  }
  return format(date, 'PP', { locale: getDateFnsLocale(language) });
}

/** Date and time (notifications). */
export function formatLocalizedDateTime(date: Date, language: string): string {
  if (usesIntlDateFormatting(language)) {
    return new Intl.DateTimeFormat(getIntlLocale(language), {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }
  return format(date, 'PPp', { locale: getDateFnsLocale(language) });
}
